/**
 * AES-256-GCM (browser impl, Web Crypto). Shares the drift-prone bits — key-derivation salt, byte layout,
 * base64 — with the server via `@shared/crypto-format` + `@shared/encoding`; only the cipher call differs
 * (asynchronous Web Crypto here vs synchronous `node:crypto` in src/shared/crypto). The pairing secret is
 * held only in the browser; Firebase sees only ciphertext. The web↔server compat test locks both impls.
 */
import { KEY_SALT_PREFIX, IV_LEN, MIN_LEN } from "@shared/crypto-format";
import { utf8ToBytes, bytesToUtf8, bytesToB64, b64ToBytes } from "@shared/encoding";
import type { Crypto } from "@shared/codec";

/** Derive the AES key from the pairing secret. MUST match src/shared/crypto.ts deriveKey + Crypto.kt. */
export async function deriveKey(pairingSecret: string): Promise<CryptoKey> {
  const hash = await crypto.subtle.digest("SHA-256", utf8ToBytes(`${KEY_SALT_PREFIX}${pairingSecret}`) as BufferSource);
  return crypto.subtle.importKey("raw", hash, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export async function encrypt(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  // Web Crypto's AES-GCM output is `ciphertext || tag(16)`, matching Node's ct+getAuthTag() concatenation.
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, utf8ToBytes(plaintext) as BufferSource),
  );
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv, 0);
  out.set(ct, iv.length);
  return bytesToB64(out);
}

export async function decrypt(key: CryptoKey, b64: string): Promise<string | null> {
  try {
    const buf = b64ToBytes(b64);
    if (buf.length < MIN_LEN) return null;
    const iv = buf.subarray(0, IV_LEN);
    const ctWithTag = buf.subarray(IV_LEN); // ciphertext || tag — Web Crypto expects them together
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, ctWithTag as BufferSource);
    return bytesToUtf8(new Uint8Array(pt));
  } catch {
    return null;
  }
}

/** The browser crypto implementation, injected into a shared `Codec`. */
export const webCrypto: Crypto<CryptoKey> = { deriveKey, encrypt, decrypt };
